import React from 'react';
import PropTypes from 'prop-types';
import { Mui } from '@osu/react-ui';

/**
 * DeclineConfirmationModal Component
 * Modal dialog for confirming the decline of an honors offer
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Function to call when modal should close
 * @param {function} onConfirm - Function to call when user confirms decline
 * @param {object} modalConfig - Configuration object with modal text
 * @param {string} modalConfig.title - Modal title
 * @param {string} modalConfig.message - Modal message
 * @param {string} modalConfig.confirmButtonText - Confirm button text
 * @param {string} modalConfig.cancelButtonText - Cancel button text
 * @param {boolean} isSubmitting - Whether the submission is in progress
 */
const DeclineConfirmationModal = ({ 
  open, 
  onClose, 
  onConfirm, 
  modalConfig,
  isSubmitting = false
}) => {
  const {
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmButtonText = 'Confirm',
    cancelButtonText = 'Cancel'
  } = modalConfig || {};

  const handleConfirm = () => {
    onConfirm();
    // Don't close modal here - let the parent handle closing after successful submission
  };

  return (
    <Mui.Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="decline-confirmation-title"
      aria-describedby="decline-confirmation-description"
    >
      <Mui.DialogTitle id="decline-confirmation-title">
        {title}
      </Mui.DialogTitle>
      
      <Mui.DialogContent>
        <Mui.DialogContentText id="decline-confirmation-description">
          {message}
        </Mui.DialogContentText>
      </Mui.DialogContent>
      
      <Mui.DialogActions sx={{ p: 3, gap: 1 }}>
        <Mui.Button
          onClick={onClose}
          variant="outlined"
          color="primary"
          disabled={isSubmitting}
        >
          {cancelButtonText}
        </Mui.Button>
        <Mui.Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          autoFocus
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : confirmButtonText}
        </Mui.Button>
      </Mui.DialogActions>
    </Mui.Dialog>
  );
};

DeclineConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  modalConfig: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    confirmButtonText: PropTypes.string,
    cancelButtonText: PropTypes.string
  }),
  isSubmitting: PropTypes.bool
};

export default DeclineConfirmationModal;